import { RefObject, useEffect, useRef } from 'react'

// Focus management hook
const _useFocusTrap = (isActive: boolean): RefObject<HTMLDivElement> => {
  const containerRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!isActive || !containerRef.current) return

    // Store the previously focused element
    previousFocusRef.current = document.activeElement as HTMLElement

    // Get all focusable elements
    const getFocusableElements = () => {
      if (!containerRef.current) return []

      const focusableSelectors = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')

      return Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(focusableSelectors),
      ).filter((el) => {
        // Check if element is visible
        const style = window.getComputedStyle(el)
        return style.display !== 'none' && style.visibility !== 'hidden'
      })
    }

    // Focus the first focusable element
    const focusableElements = getFocusableElements()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }

    // Handle tab key navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !containerRef.current) return

      const focusableElements = getFocusableElements()
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      // Tab backwards
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      }
      // Tab forwards
      else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)

      // Restore focus to the previously focused element
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus()
      }
    }
  }, [isActive])

  return containerRef
}

// Announce changes for screen readers
const _useAriaLiveRegion = () => {
  const regionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // Create an aria-live region
    const region = document.createElement('div')
    region.setAttribute('aria-live', 'polite')
    region.setAttribute('aria-atomic', 'true')
    region.style.position = 'absolute'
    region.style.left = '-10000px'
    region.style.width = '1px'
    region.style.height = '1px'
    region.style.overflow = 'hidden'

    document.body.appendChild(region)
    regionRef.current = region

    return () => {
      if (regionRef.current && document.body.contains(regionRef.current)) {
        document.body.removeChild(regionRef.current)
      }
    }
  }, [])

  const announce = (message: string) => {
    if (regionRef.current) {
      regionRef.current.textContent = message

      // Clear after announcement
      setTimeout(() => {
        if (regionRef.current) {
          regionRef.current.textContent = ''
        }
      }, 1000)
    }
  }

  return { announce }
}

// Keyboard navigation for lists and grids
const _useArrowKeyNavigation = (
  items: unknown[],
  onSelect: (item: unknown, index: number) => void,
  options?: {
    horizontal?: boolean
    wrap?: boolean
    onEscape?: () => void
  },
) => {
  const selectedIndexRef = useRef<number>(-1)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const currentIndex = selectedIndexRef.current
      let newIndex = currentIndex

      switch (e.key) {
        case 'ArrowDown':
          if (!options?.horizontal) {
            e.preventDefault()
            newIndex = currentIndex + 1
          }
          break
        case 'ArrowUp':
          if (!options?.horizontal) {
            e.preventDefault()
            newIndex = currentIndex - 1
          }
          break
        case 'ArrowRight':
          if (options?.horizontal) {
            e.preventDefault()
            newIndex = currentIndex + 1
          }
          break
        case 'ArrowLeft':
          if (options?.horizontal) {
            e.preventDefault()
            newIndex = currentIndex - 1
          }
          break
        case 'Enter':
        case ' ':
          if (currentIndex >= 0 && currentIndex < items.length) {
            e.preventDefault()
            onSelect(items[currentIndex], currentIndex)
          }
          break
        case 'Escape':
          if (options?.onEscape) {
            e.preventDefault()
            options.onEscape()
          }
          break
        default:
          return
      }

      // Handle wrapping
      if (options?.wrap) {
        if (newIndex < 0) newIndex = items.length - 1
        if (newIndex >= items.length) newIndex = 0
      } else {
        newIndex = Math.max(0, Math.min(items.length - 1, newIndex))
      }

      if (
        newIndex !== currentIndex &&
        newIndex >= 0 &&
        newIndex < items.length
      ) {
        selectedIndexRef.current = newIndex
        onSelect(items[newIndex], newIndex)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [items, onSelect, options])

  const setSelectedIndex = (index: number) => {
    selectedIndexRef.current = index
  }

  return { setSelectedIndex }
}

// Skip to main content link
export const useSkipToMain = () => {
  useEffect(() => {
    const skipLink = document.createElement('a')
    skipLink.href = '#main-content'
    skipLink.className = 'skip-to-main'
    skipLink.textContent = 'Skip to main content'

    // Style the skip link
    skipLink.style.position = 'absolute'
    skipLink.style.left = '-10000px'
    skipLink.style.top = 'auto'
    skipLink.style.width = '1px'
    skipLink.style.height = '1px'
    skipLink.style.overflow = 'hidden'

    // Show on focus
    skipLink.addEventListener('focus', () => {
      skipLink.style.position = 'fixed'
      skipLink.style.left = '10px'
      skipLink.style.top = '10px'
      skipLink.style.width = 'auto'
      skipLink.style.height = 'auto'
      skipLink.style.padding = '8px 16px'
      skipLink.style.backgroundColor = '#1890ff'
      skipLink.style.color = 'white'
      skipLink.style.textDecoration = 'none'
      skipLink.style.borderRadius = '4px'
      skipLink.style.zIndex = '9999'
    })

    skipLink.addEventListener('blur', () => {
      skipLink.style.position = 'absolute'
      skipLink.style.left = '-10000px'
      skipLink.style.width = '1px'
      skipLink.style.height = '1px'
    })

    document.body.insertBefore(skipLink, document.body.firstChild)

    return () => {
      if (document.body.contains(skipLink)) {
        document.body.removeChild(skipLink)
      }
    }
  }, [])
}
